import React, { useEffect } from "react";
import loadable from "@loadable/component";
import { useBnbSelector } from "../../redux/store";
import { NavStep } from "../../../shared-logic/utils/Navigation";
import BuildNavigationItems from "./common/BuildNavigationItems";
import { useModalMap } from "../../../../../common-deprecated/containers/ModalProvider";
import { aemLexusBnbModalMap } from "./modals/modals";
import useInit from "../../hooks/shared/init/useInit";
import CustomizeContainer from "./step-customize/CustomizeContainer";
import useStatus from "../../../../../common-deprecated/hooks/useStatus";
import ServicesContainer from "./step-services/ServicesContainer";
import BuyContainer from "./step-buy/BuyContainer";
import BuildNavigation from "./common/nav/BuildNavigation";
import SummaryContainer from "./step-summary/SummaryContainer";
import MobileFooter from "./common/nav/MobileFooter";
import SubModelOverview from "./step-submodel/SubModelOverview";
import SelectGrade from "./step-grades/SelectGrade";
import { MODAL_FINANCE_INFO } from "../../utils/modalConstants";
import ReserveContainer from "./step-reserve/ReserveContainer";
import { enableCheckoutSteps, showServicesStep } from "../../utils/settings/bnbSettingUtils";
import LeaseView from "./step-lease/LeaseView";
import useBuildTools from "../../hooks/useBuildTools";
import useDataFetcher from "../../../../../common-deprecated/hooks/useDataFetcher";
import type { GetFetchesFnType } from "../../../../../common-deprecated/utils/registryTypes";
import { COMPONENT_BUILDANDBUY } from "../../../../../shared-logic/server/components";
import { getBuildComponentFetches, buildFetchDispatcher } from "../../datafetch/fetchUtils";
import { bnbPostFetch } from "../../datafetch/postFetch";
import NotificationContainer from "../../../../../common-deprecated/components/notification/lexus/NotificationContainer";
import GlobalStyle from "../../../../../common-deprecated/styles/v2/lexus/globals/GlobalStyle";
import { updateTrackingLayerVersioning } from "../../utils/tracking";

const IntegratedCompareV2 = loadable(async () => {
    const component = await import(
        /* webpackChunkName: "integrated-compare-aem-lexus" */
        "../../../../compare/components/lexus/IntegratedCompareLexus"
    );
    return component.default;
});

const BuildAndBuyV2 = (): JSX.Element => {
    useInit();
    useModalMap(aemLexusBnbModalMap);

    useEffect(() => {
        updateTrackingLayerVersioning();
    }, []);

    const step = useBnbSelector((state) => state.navigation.step);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const retailerSettings = useBnbSelector((state) => state.retailerSettings);

    const showServices = showServicesStep(bnbSettings, retailerSettings);
    const stepTotal = 6 - (showServices ? 0 : 1) - (enableCheckoutSteps(bnbSettings, retailerSettings) ? 0 : 1);

    useDataFetcher(
        getBuildComponentFetches as unknown as GetFetchesFnType,
        buildFetchDispatcher,
        COMPONENT_BUILDANDBUY,
        [],
        [],
        bnbPostFetch,
    );

    const buildLinks = useBuildTools();
    useStatus(commonSettings, "AEM Lexus Build", buildLinks, [{ key: "f", settings: { type: MODAL_FINANCE_INFO } }]);

    const stepNumbers: Partial<Record<NavStep, number>> = {
        [NavStep.Submodels]: 1,
        [NavStep.Grades]: 2,
        [NavStep.Customize]: 3,
        [NavStep.Services]: showServices ? 4 : 0,
        [NavStep.Summary]: showServices ? 5 : 4,
        [NavStep.Buy]: showServices ? 6 : 5,
        [NavStep.Reserve]: showServices ? 6 : 5,
        [NavStep.Lease]: showServices ? 6 : 5,
    };

    let component;
    switch (step) {
        case NavStep.Submodels:
            component = <SubModelOverview />;
            break;

        case NavStep.Grades:
            component = <SelectGrade />;
            break;

        case NavStep.Customize:
            component = <CustomizeContainer />;
            break;

        case NavStep.Services:
            component = <ServicesContainer />;
            break;

        case NavStep.Summary:
            component = <SummaryContainer />;
            break;

        case NavStep.Buy:
            component = <BuyContainer />;
            break;

        case NavStep.Reserve:
            component = <ReserveContainer />;
            break;

        case NavStep.Lease:
            component = <LeaseView />;
            break;
    }

    return (
        <>
            <GlobalStyle />
            <BuildNavigation stepNumbers={stepNumbers} stepTotal={stepTotal}>
                <BuildNavigationItems />
            </BuildNavigation>
            {component}
            <MobileFooter />
            {[NavStep.Submodels, NavStep.Grades].includes(step) && <IntegratedCompareV2 />}
            <NotificationContainer />
        </>
    );
};

export default BuildAndBuyV2;
